import { runInAction } from "mobx";
import { authSessionStore } from "@/features/auth/stores/auth-session.store";
import { dashboardService } from "../services/dashboard.service";
import { dashboardFiltersStore } from "../stores/filters.store";
import { dashboardKpiStore } from "../stores/kpi.store";
import { buildDashboardScopePayload } from "../utils/dashboard-request-scope";
import { resolveSelectedReportingPeriod } from "../utils/reporting-years";

// One in-flight request per distinct payload: a re-trigger with identical
// params joins the pending request instead of paying the server twice.
// The key carries the authenticated identity so a session change can never
// join a request issued under the previous user's token.
let inFlight = { key: null, promise: null };

export const loadKpiSummary = () => {
  const { years, frameworks } = dashboardFiltersStore.filters;
  const reportingPeriod = resolveSelectedReportingPeriod(
    years,
    dashboardFiltersStore.available.reportingYears,
  );
  const body = {
    years,
    frameworks,
    ...(reportingPeriod || {}),
    ...buildDashboardScopePayload({
      filters: dashboardFiltersStore.filters,
      orgTree: dashboardFiltersStore.available.orgTree,
    }),
  };
  // The store user is the API's safeUser() serialization: it has userId,
  // never _id.
  const { user } = authSessionStore;
  const key = JSON.stringify({
    userId: user?.userId ?? null,
    companyId: user?.companyId ?? null,
    body,
  });
  if (inFlight.promise && inFlight.key === key) return inFlight.promise;

  // Params differ from any pending request: newest wins, older responses
  // are dropped via the sequence check instead of overwriting fresh data.
  const seq = dashboardKpiStore.nextRequestSeq();
  dashboardKpiStore.setLoading(true);
  const promise = (async () => {
    try {
      const data = await dashboardService.getKpiSummary(body);
      if (dashboardKpiStore.isStaleSeq(seq)) return;
      runInAction(() => {
        dashboardKpiStore.setSummary(data);
      });
    } finally {
      // Identity check on the promise, not the key: in an A,B,A overlap the
      // first A must not clear the second A's still-pending entry.
      if (inFlight.promise === promise) inFlight = { key: null, promise: null };
      if (!dashboardKpiStore.isStaleSeq(seq)) {
        dashboardKpiStore.setLoading(false);
      }
    }
  })();
  inFlight = { key, promise };
  return promise;
};
